import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Collections, Product, StockMovement, StockMovementType, UserRole } from '../../types';
import { Search, Box, Calculator, FileDown, ChevronDown, Store, Archive } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface StockReportProps {
    products: Product[];
    collections: Collections[];
    movements: StockMovement[];
    userRole: UserRole | null;
}

type ReportTab = 'Unified' | 'StoreShelf' | 'WarehouseReserve';
type PDFMode = 'All' | 'Store' | 'Warehouse';

const StockReport: React.FC<StockReportProps> = ({ products, collections, movements, userRole }) => {
    const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
    const pdfDropdownRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState<ReportTab>('Unified');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCollection, setFilterCollection] = useState('All');
    const [expandedArticles, setExpandedArticles] = useState<Set<string>>(new Set());

    const canSeeValuation = userRole === 'Admin' || userRole === 'Viewer';
    const colSpanCount = activeTab === 'Unified'
        ? (canSeeValuation ? 5 : 4)
        : (canSeeValuation ? 4 : 3);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (pdfDropdownRef.current && !pdfDropdownRef.current.contains(e.target as Node)) {
                setPdfDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleArticle = (id: string) => {
        const next = new Set(expandedArticles);
        if (next.has(id)) next.delete(id); else next.add(id);
        setExpandedArticles(next);
    };

    const reportData = useMemo(() => {
        return products
            .filter(p => {
                const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.id.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesColl = filterCollection === 'All' || p.collection === filterCollection;
                return matchesSearch && matchesColl;
            })
            .map(p => {
                const variants = (p.stocks || []).map(s => ({
                    ...s,
                    storeValue: s.stock          * p.price,
                    whValue:    s.warehouseStock  * p.price,
                    value:      (s.stock + s.warehouseStock) * p.price,
                }));
                const totalStore      = variants.reduce((acc, v) => acc + v.stock,         0);
                const totalWH         = variants.reduce((acc, v) => acc + v.warehouseStock, 0);
                const totalValue      = variants.reduce((acc, v) => acc + v.value,          0);
                // ── Only sum variants that actually have stock in that location ──
                const totalStoreValue = variants.reduce((acc, v) => v.stock         > 0 ? acc + v.storeValue : acc, 0);
                const totalWHValue    = variants.reduce((acc, v) => v.warehouseStock > 0 ? acc + v.whValue   : acc, 0);
                return { ...p, variants, totalStore, totalWH, totalValue, totalStoreValue, totalWHValue };
            });
    }, [products, searchTerm, filterCollection]);

    const filteredReportData = useMemo(() => {
        if (activeTab === 'StoreShelf')       return reportData.filter(d => d.totalStore > 0);
        if (activeTab === 'WarehouseReserve') return reportData.filter(d => d.totalWH    > 0);
        return reportData.filter(d => d.totalStore > 0 || d.totalWH > 0);
    }, [reportData, activeTab]);

    const totals = useMemo(() => ({
        store: filteredReportData.reduce((acc, d) => acc + d.totalStore, 0),
        wh:    filteredReportData.reduce((acc, d) => acc + d.totalWH,    0),
        value:
            activeTab === 'StoreShelf'       ? filteredReportData.reduce((acc, d) => acc + d.totalStoreValue, 0) :
            activeTab === 'WarehouseReserve' ? filteredReportData.reduce((acc, d) => acc + d.totalWHValue,    0) :
            filteredReportData.reduce((acc, d) => acc + d.totalValue, 0),
    }), [filteredReportData, activeTab]);

    const handleDownloadPDF = (mode: PDFMode) => {
        const doc = new jsPDF('l', 'mm', 'a4');

        const label =
            mode === 'Store'     ? 'StoreShelf' :
            mode === 'Warehouse' ? 'WarehouseReserve' :
            'Unified';

        doc.setFontSize(18);
        doc.text(`Stock Ledger — ${label}`, 14, 20);
        doc.setFontSize(9);
        doc.text(
            `Audit Trail Generated: ${new Date().toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '-')}`,
            14, 28
        );

        const targetData =
            mode === 'Store'     ? reportData.filter(d => d.totalStore > 0) :
            mode === 'Warehouse' ? reportData.filter(d => d.totalWH    > 0) :
            reportData.filter(d => d.totalStore > 0 || d.totalWH > 0);

        const head: string[][] = [[
            'Article',
            'Size',
            ...(mode !== 'Store'     ? ['Qty (WH)']      : []),
            ...(mode !== 'Warehouse' ? ['Qty (In-Store)'] : []),
            ...(canSeeValuation      ? ['Valuation']      : []),
        ]];

        const body: (string | number)[][] = [];

        // ── Fixed: plain data loop, no JSX ──────────────────────────────────
        targetData.forEach(d => {
            d.variants
                .filter(v =>
                    mode === 'Store'     ? v.stock         > 0 :
                    mode === 'Warehouse' ? v.warehouseStock > 0 :
                    (v.stock > 0 || v.warehouseStock > 0)
                )
                .forEach(v => {
                    const row: (string | number)[] = [d.name, v.size];
                    if (mode !== 'Store')     row.push(v.warehouseStock);
                    if (mode !== 'Warehouse') row.push(v.stock);
                    if (canSeeValuation) {
                        const modeValue =
                            mode === 'Store'     ? v.storeValue :
                            mode === 'Warehouse' ? v.whValue    :
                            v.value;
                        row.push(`Rs. ${modeValue.toLocaleString()}`);
                    }
                    body.push(row);
                });
        });

        const valuationColIndex = head[0].length - 1;
        const columnStyles: Record<number, object> = {};
        head[0].forEach((_, i) => {
            if (i >= 2) columnStyles[i] = { halign: i === valuationColIndex && canSeeValuation ? 'right' : 'center' };
        });

        autoTable(doc, {
            startY: 35,
            head,
            body,
            headStyles: { fillColor: [31, 41, 55], fontSize: 8, fontStyle: 'bold' },
            styles: { fontSize: 8, cellPadding: 3 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles,
        });

        if (canSeeValuation) {
            const targetTotals = targetData.reduce((acc, d) => {
                return acc + (
                    mode === 'Store'     ? d.totalStoreValue :
                    mode === 'Warehouse' ? d.totalWHValue    :
                    d.totalValue
                );
            }, 0);

            const finalY = (doc as any).lastAutoTable.finalY ?? 35;
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(
                `Total Valuation: Rs. ${targetTotals.toLocaleString()}`,
                doc.internal.pageSize.getWidth() - 14,
                finalY + 10,
                { align: 'right' }
            );
        }

        doc.save(`NiaMia-StockLedger-${label}-${new Date().toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '-')}.pdf`);
    };

    return (
        <div className="p-8 h-full overflow-y-auto bg-gray-50 dark:bg-gray-950 transition-colors duration-200">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* ── Header ── */}
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tight leading-none">Stock Ledger</h1>
                        <div className="flex items-center gap-1 mt-4 p-1 bg-gray-200/50 dark:bg-gray-800 rounded-xl w-fit border border-gray-200 dark:border-gray-700">
                            {(['Unified', 'StoreShelf', 'WarehouseReserve'] as ReportTab[]).map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-white dark:bg-gray-700 text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    {tab === 'StoreShelf' ? 'Store Shelf' : tab === 'WarehouseReserve' ? 'WH Reserve' : 'Unified Flow'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ── PDF Download Dropdown ── */}
                    <div className="relative" ref={pdfDropdownRef}>
                        <button
                            onClick={(e) => { e.stopPropagation(); setPdfDropdownOpen(prev => !prev); }}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-lg"
                        >
                            <FileDown size={16} />
                            Download PDF
                            <ChevronDown size={14} className={`transition-transform duration-200 ${pdfDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {pdfDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-xl z-50 overflow-hidden">
                                {(
                                    [
                                        { mode: 'All'       as PDFMode, label: 'All (Unified)',  icon: <FileDown size={14} />, color: 'text-gray-700 dark:text-gray-200' },
                                        { mode: 'Store'     as PDFMode, label: 'Store Only',     icon: <Store    size={14} />, color: 'text-amber-600'  },
                                        { mode: 'Warehouse' as PDFMode, label: 'Warehouse Only', icon: <Archive  size={14} />, color: 'text-indigo-600' },
                                    ]
                                ).map(({ mode, label, icon, color }) => (
                                    <button
                                        key={mode}
                                        onClick={(e) => { e.stopPropagation(); handleDownloadPDF(mode); setPdfDropdownOpen(false); }}
                                        className={`w-full flex items-center gap-3 px-5 py-3 text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${color}`}
                                    >
                                        {icon}
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </header>

                {/* ── KPI Cards ── */}
                {/* Unified: show both qty cards + valuation */}
                {/* StoreShelf: show only store card + valuation */}
                {/* WarehouseReserve: show only WH card + valuation */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                    {activeTab !== 'WarehouseReserve' && (
                        <div className="bg-white dark:bg-gray-900 p-6 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm">
                            <div className="flex items-center gap-2 text-amber-600 mb-2">
                                <Store size={16} />
                                <span className="text-[9px] font-black uppercase tracking-widest">Current Store Balance</span>
                            </div>
                            <div className="text-3xl font-black dark:text-white tracking-tighter">
                                {totals.store} <span className="text-xs text-gray-400">UNITS</span>
                            </div>
                        </div>
                    )}

                    {activeTab !== 'StoreShelf' && (
                        <div className="bg-white dark:bg-gray-900 p-6 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm">
                            <div className="flex items-center gap-2 text-indigo-600 mb-2">
                                <Archive size={16} />
                                <span className="text-[9px] font-black uppercase tracking-widest">Warehouse Reserve</span>
                            </div>
                            <div className="text-3xl font-black dark:text-white tracking-tighter">
                                {totals.wh} <span className="text-xs text-gray-400">UNITS</span>
                            </div>
                        </div>
                    )}

                    {canSeeValuation && (
                        <div className="bg-indigo-600 p-6 rounded-[2rem] text-white shadow-xl shadow-indigo-100 dark:shadow-none">
                            <div className="flex items-center justify-between opacity-80 mb-2">
                                <span className="text-[9px] font-black uppercase tracking-widest">
                                    {activeTab === 'StoreShelf'       ? 'Store Valuation'     :
                                     activeTab === 'WarehouseReserve' ? 'Warehouse Valuation' :
                                     'Gross Net Valuation'}
                                </span>
                                <Calculator size={18} />
                            </div>
                            <div className="text-2xl font-black tracking-tighter">₨ {totals.value.toLocaleString()}</div>
                        </div>
                    )}
                </div>

                {/* ── Filters ── */}
                <div className="bg-white dark:bg-gray-900 p-5 rounded-[1.5rem] border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1 group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="Search Article Code or Name..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-6 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl font-black text-[11px] uppercase tracking-widest outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all"
                        />
                    </div>
                    <select
                        value={filterCollection}
                        onChange={e => setFilterCollection(e.target.value)}
                        className="px-6 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl font-black text-[11px] uppercase tracking-widest outline-none cursor-pointer border-none"
                    >
                        <option value="All">All Stream Collections</option>
                        {collections.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                </div>

                {/* ── Table ── */}
                <div className="bg-white dark:bg-gray-900 rounded-[2.5rem] border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50/50 dark:bg-gray-800/50 text-[10px] font-black uppercase text-gray-400 tracking-[0.2em] border-b border-gray-100 dark:border-gray-800">
                                <tr>
                                    <th className="p-6 w-10"></th>
                                    <th className="p-6">Article</th>
                                    {activeTab !== 'StoreShelf'       && <th className="p-6 text-center">QTY (WH)</th>}
                                    {activeTab !== 'WarehouseReserve' && <th className="p-6 text-center">QTY (Store)</th>}
                                    {canSeeValuation && <th className="p-6 text-right">Asset Value</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                                {filteredReportData.map(d => {
                                    const isExpanded = expandedArticles.has(d.id);
                                    return (
                                        <React.Fragment key={d.id}>
                                            {/* ── Product row ── */}
                                            <tr
                                                onClick={() => toggleArticle(d.id)}
                                                className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors cursor-pointer"
                                            >
                                                <td className="p-6">
                                                    <ChevronDown size={16} className={`transition-transform duration-300 ${isExpanded ? 'rotate-180 text-indigo-500' : 'text-gray-300'}`} />
                                                </td>
                                                <td className="p-6">
                                                    <div className="text-xs font-black dark:text-white uppercase truncate max-w-[240px]">{d.name}</div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[9px] font-black text-indigo-500 uppercase tracking-tighter">#{d.id}</span>
                                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">• {d.collection}</span>
                                                    </div>
                                                </td>
                                                {activeTab !== 'StoreShelf' && (
                                                    <td className="p-6 text-center font-black text-sm text-gray-500 dark:text-gray-400">{d.totalWH}</td>
                                                )}
                                                {activeTab !== 'WarehouseReserve' && (
                                                    <td className={`p-6 text-center font-black text-sm ${d.totalStore < 5 ? 'text-rose-500' : 'text-gray-900 dark:text-white'}`}>
                                                        {d.totalStore}
                                                    </td>
                                                )}
                                                {canSeeValuation && (
                                                    <td className="p-6 text-right font-black text-sm dark:text-white font-mono">
                                                        ₨ {(
                                                            activeTab === 'StoreShelf'       ? d.totalStoreValue :
                                                            activeTab === 'WarehouseReserve' ? d.totalWHValue    :
                                                            d.totalValue
                                                        ).toLocaleString()}
                                                    </td>
                                                )}
                                            </tr>

                                            {/* ── Expanded variant rows ── */}
                                            <tr>
                                                <td colSpan={colSpanCount} className="p-0 border-none">
                                                    <div className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                                        <div className="overflow-hidden">
                                                            <table className="w-full border-t border-gray-100 dark:border-gray-800">
                                                                <tbody>
                                                                    {/* ── Filter out 0-qty variants per tab ── */}
                                                                    {d.variants
                                                                        .filter(v =>
                                                                            activeTab === 'StoreShelf'       ? v.stock         > 0 :
                                                                            activeTab === 'WarehouseReserve' ? v.warehouseStock > 0 :
                                                                            (v.stock > 0 || v.warehouseStock > 0)
                                                                        )
                                                                        .map((v, vIdx) => (
                                                                            <tr key={vIdx} className="bg-indigo-50/30 dark:bg-indigo-900/5 border-b border-gray-100 dark:border-gray-800 last:border-0">
                                                                                <td className="w-10 pl-6"></td>
                                                                                <td className="py-3 pl-10 pr-6">
                                                                                    <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">
                                                                                        {d.name} — Size {v.size}
                                                                                    </span>
                                                                                </td>
                                                                                {activeTab !== 'StoreShelf' && (
                                                                                    <td className="py-3 px-6 text-center text-[10px] font-black text-gray-500 dark:text-gray-400">{v.warehouseStock}</td>
                                                                                )}
                                                                                {activeTab !== 'WarehouseReserve' && (
                                                                                    <td className={`py-3 px-6 text-center text-[10px] font-black ${v.stock < 5 ? 'text-rose-500' : 'text-gray-700 dark:text-gray-300'}`}>
                                                                                        {v.stock}
                                                                                        {v.stock < 5 && <span className="ml-1.5 inline-block w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse align-middle" />}
                                                                                    </td>
                                                                                )}
                                                                                {canSeeValuation && (
                                                                                    <td className="py-3 px-6 text-right text-[10px] font-mono font-black text-gray-500 dark:text-gray-400">
                                                                                        ₨ {(
                                                                                            activeTab === 'StoreShelf'       ? v.storeValue :
                                                                                            activeTab === 'WarehouseReserve' ? v.whValue    :
                                                                                            v.value
                                                                                        ).toLocaleString()}
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        ))
                                                                    }
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                })}

                                {/* ── Valuation total footer ── */}
                                {canSeeValuation && filteredReportData.length > 0 && (
                                    <tr className="bg-indigo-50/50 dark:bg-indigo-900/10 border-t-2 border-indigo-100 dark:border-indigo-900/30">
                                        <td
                                            colSpan={activeTab === 'Unified' ? 4 : 3}
                                            className="p-6 text-right text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest"
                                        >
                                            Total Valuation
                                        </td>
                                        <td className="p-6 text-right font-black text-lg text-indigo-600 dark:text-indigo-400 font-mono">
                                            ₨ {totals.value.toLocaleString()}
                                        </td>
                                    </tr>
                                )}

                                {/* ── Empty state ── */}
                                {filteredReportData.length === 0 && (
                                    <tr>
                                        <td colSpan={colSpanCount} className="p-32 text-center">
                                            <div className="flex flex-col items-center justify-center text-gray-300 dark:text-gray-700">
                                                <div className="p-8 bg-gray-50 dark:bg-gray-800 rounded-full mb-4 shadow-inner">
                                                    <Box size={48} className="opacity-10" />
                                                </div>
                                                <p className="text-[10px] font-black uppercase tracking-[0.2em]">Zero Audit Results</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default StockReport;